import type { ReactNode } from 'react';
import { useImmer } from 'use-immer';

import { CDF_IDENTITY_TABLE } from '#core/lib/cognite/identity.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';
import { canonicalOpTable } from '#core/lib/expr/op-surface.ts';
import { KNOWLEDGE_GRAPH_GROUP, TIMESERIES_GROUP } from '#core/lib/expr/providers/groups.ts';
import { useCdfViewProvider } from '#core/lib/expr/providers/useCdfViewProvider.ts';
import { type ProviderEntry, useProviderRegistry } from '#core/lib/expr/providers/useProviderRegistry.ts';
import { type EvalDeps, EvalDepsContext } from '#core/lib/expr/session/EvalDepsContext.ts';

import { useSessionTimeseries } from '#plugin-chat/session/useSessionTimeseries.ts';

export interface EvalDepsProviderProps {
  readonly children: ReactNode;
  readonly views: readonly DataModelView[];
}

export function EvalDepsProvider({ children, views }: EvalDepsProviderProps) {
  // Application providers (the iPSO virtual views) register themselves at
  // runtime from INSIDE the session via `useAppProvider`, so the catalog
  // hooks that build them can read session context. Holding them in state
  // (not a ref) makes the catalogue reactive: registration re-renders the
  // session, so render-time consumers — the DescribeViews `viewNames` enum —
  // recompute. `register`/`unregister` are created once with stable
  // identities and bail when the entry is unchanged, so `useAppProvider`'s
  // effect never loops even without React Compiler memoization.
  const [appProviders, setAppProviders] = useImmer<ReadonlyMap<string, ProviderEntry>>(new Map());

  const timeseriesProvider = useSessionTimeseries();
  const cdfViewProvider = useCdfViewProvider({ views, mintSeries: timeseriesProvider.buildSource });

  // App providers lead each group so first-`claims` resolution gives them
  // priority over the built-in CDF view / timeseries providers. The single
  // resolution path lives in `buildProviderRegistry` — not duplicated here.
  const appIn = (group: string): readonly ProviderEntry[] => [
    ...appProviders.values().filter((e) => e.provider.group === group),
  ];

  const registry = useProviderRegistry({
    [KNOWLEDGE_GRAPH_GROUP]: [
      ...appIn(KNOWLEDGE_GRAPH_GROUP),
      { provider: cdfViewProvider, names: views.map((v) => v.externalId) },
    ],
    [TIMESERIES_GROUP]: [...appIn(TIMESERIES_GROUP), { provider: timeseriesProvider, names: ['datapoints'] }],
  });

  const contextValue: EvalDeps = {
    ops: canonicalOpTable,
    // The provider/session boundary is where the CDF identity contract is
    // threaded onto the eval context — without it the engine falls back to
    // the empty table and direct-relation cells are recognised nowhere
    // (identity-keyed joins on untyped verb outputs, set-ref interning).
    identities: CDF_IDENTITY_TABLE,
    getProvider: registry.getProvider,
    getProviderById: registry.getProviderById,
    viewNames: registry.claimedNames(KNOWLEDGE_GRAPH_GROUP),
    registerProvider(entries) {
      setAppProviders((draft) => {
        for (const entry of entries) {
          if (draft.get(entry.provider.id) !== entry) {
            draft.set(entry.provider.id, entry);
          }
        }
      });

      return () => {
        setAppProviders((draft) => {
          for (const entry of entries) {
            draft.delete(entry.provider.id);
          }
        });
      };
    },
  };

  return <EvalDepsContext value={contextValue}>{children}</EvalDepsContext>;
}
